'use client'

import { useEffect, useState } from 'react'

import { type PortableTextBlock, stegaClean } from 'next-sanity'

import { Icon } from '@/app/components/Icon'
import PortableText from '@/app/components/PortableText'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import type { Advertiser, PartnerQuote as PartnerQuoteProps, Publisher } from '@/sanity.types'
import { urlForImage } from '@/sanity/lib/utils'

type Partner = Pick<Publisher, 'icon' | 'publisherName'> | Pick<Advertiser, 'logo' | 'companyName'>

type PartnerQuoteComponentProps = {
  block: Omit<PartnerQuoteProps, 'advertiser' | 'publisher'> & {
    _key?: string
    publisher?: Partner | null
    advertiser?: Partner | null
  }
  index: number
  pageId?: string
  pageType?: string
  isLast?: boolean
}

export default function PartnerQuote({
  block,
  pageId,
  pageType,
  isLast,
}: PartnerQuoteComponentProps) {
  const [svgContent, setSvgContent] = useState<string | null>(null)

  const partnerType = stegaClean(block.partnerType)
  const partner = partnerType === 'publisher' ? block.publisher : block.advertiser

  let companyName: string | undefined
  if (partnerType === 'publisher' && partner && 'publisherName' in partner) {
    companyName = partner.publisherName
  } else if (partnerType === 'advertiser' && partner && 'companyName' in partner) {
    companyName = partner.companyName
  }

  // Fetch SVG for advertiser logos
  useEffect(() => {
    if (partnerType === 'advertiser' && partner && 'logo' in partner && partner.logo) {
      const imageUrl = urlForImage(partner.logo)?.url()
      if (imageUrl) {
        fetch(imageUrl)
          .then((res) => res.text())
          .then((svg) => setSvgContent(svg))
          .catch((err) => console.error('Failed to fetch advertiser logo:', err))
      }
    }
  }, [partnerType, partner])

  if (!block?.content?.length) return null
  if (!partner || !companyName) return null

  return (
    <div className={cn('content-container my-10xl md:my-12xl')}>
      <div className="mx-auto max-w-[1200px] flex flex-col items-center gap-7xl self-stretch rounded-tr-5xl rounded-bl-5xl bg-paved-blue-700 py-10xl px-xl md:p-10xl">
        <div className="flex flex-col gap-7xl w-full lg:flex-row lg:justify-between lg:max-w-[1200px]">
          <div className="flex flex-col justify-center max-w-[660px] mx-auto lg:mx-0 gap-xl lg:flex-col-reverse">
            <p className="inline-flex size-body-m py-s px-m mx-auto lg:ml-0 text-paved-white font-book rounded-tr-md rounded-bl-md bg-paved-blue-600">
              {companyName}
            </p>
            <PortableText
              className="text-center [&_*]:!text-paved-white lg:text-left"
              tracking={{ location: TRACKING_LOCATIONS.PAGE_CONTENT }}
              value={block.content as PortableTextBlock[]}
              documentId={pageId}
              documentType={pageType}
              parentPath={block._key ? `pageBuilder[_key=="${block._key}"].content` : undefined}
            />
          </div>
          <div className="flex flex-col items-center lg:justify-center h-[64px]">
            {partnerType === 'advertiser' && svgContent && (
              <div
                className="text-paved-white h-full flex items-center justify-center"
                dangerouslySetInnerHTML={{
                  __html: svgContent.replace(/fill="[^"]*"/g, 'fill="currentColor"'),
                }}
              />
            )}
            {partnerType === 'publisher' && partner && 'icon' in partner && partner.icon && (
              <div className="text-paved-white h-full flex items-center justify-center">
                <Icon
                  icon={{
                    _type: 'icon',
                    provider: partner.icon.provider ?? null,
                    style: partner.icon.style ?? null,
                    name: partner.icon.name ?? null,
                  }}
                  size={64}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
